import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import "./AdminReviewsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, approved
  const [deleteModal, setDeleteModal] = useState(null);
  const [actionLoading, setActionLoading] = useState({
    approve: null,
    reject: null,
    delete: null
  });

  // Helper for authorized fetch
  const apiFetch = async (path, token, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    return res;
  };

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    // Admin guard
    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchReviews(token);
  }, [navigate]);

  const fetchReviews = async (token) => {
    try {
      setLoading(true);
      const response = await apiFetch("/admin/reviews", token);
      
      if (response.status === 401 || response.status === 403) {
        navigate("/");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReviews(data.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReview = async (reviewId) => {
    setActionLoading(prev => ({ ...prev, approve: reviewId }));
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    
    try {
      const response = await apiFetch(`/admin/reviews/${reviewId}`, token, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: true }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update the review in state
          setReviews(prev => 
            prev.map(review => 
              review.id === reviewId 
                ? { ...review, status: true }
                : review
            )
          );
          toast.success("Review approved successfully!");
        }
      } else {
        toast.error("Failed to approve review");
      }
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error("Error approving review");
    } finally {
      setActionLoading(prev => ({ ...prev, approve: null }));
    }
  };

  const handleRejectReview = async (reviewId) => {
    setActionLoading(prev => ({ ...prev, reject: reviewId }));
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    
    try {
      const response = await apiFetch(`/admin/reviews/${reviewId}`, token, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: false }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update the review in state
          setReviews(prev => 
            prev.map(review => 
              review.id === reviewId 
                ? { ...review, status: false }
                : review
            )
          );
          toast.success("Review rejected successfully!");
        }
      } else {
        toast.error("Failed to reject review");
      }
    } catch (error) {
      console.error("Error rejecting review:", error);
      toast.error("Error rejecting review");
    } finally {
      setActionLoading(prev => ({ ...prev, reject: null }));
    }
  };

  const handleDeleteReview = async (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    setDeleteModal(review);
  };

  const confirmDeleteReview = async () => {
    if (!deleteModal) return;
    
    setActionLoading(prev => ({ ...prev, delete: deleteModal.id }));
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    
    try {
      const response = await apiFetch(`/admin/reviews/${deleteModal.id}`, token, {
        method: "DELETE",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Remove the review from state
          setReviews(prev => prev.filter(review => review.id !== deleteModal.id));
          toast.success("Review deleted successfully!");
          setDeleteModal(null);
        }
      } else {
        toast.error("Failed to delete review");
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("Error deleting review");
    } finally {
      setActionLoading(prev => ({ ...prev, delete: null }));
    }
  };


  const renderStars = (count) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < count ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === "pending") return !review.status;
    if (filter === "approved") return review.status;
    return true; // all
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-reviews-page">
          <div className="loading">Loading reviews...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-reviews-page">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Reviews Management</h1>
            <p>Manage product reviews and ratings</p>
          </div>
          <div className="admin-actions">
            <button className="btn-secondary" onClick={() => navigate("/admin")}>
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Reviews ({reviews.length})
          </button>
          <button 
            className={`filter-tab ${filter === "pending" ? "active" : ""}`}
            onClick={() => setFilter("pending")}
          >
            Pending ({reviews.filter(r => !r.status).length})
          </button>
          <button 
            className={`filter-tab ${filter === "approved" ? "active" : ""}`}
            onClick={() => setFilter("approved")}
          >
            Approved ({reviews.filter(r => r.status).length})
          </button>
        </div>

        {/* Reviews List */}
        <div className="reviews-list">
          {filteredReviews.length === 0 ? (
            <div className="empty-state">
              <p>No reviews found for the selected filter.</p>
            </div>
          ) : (
            filteredReviews.map((review) => (
              <div key={review.id} className={`review-card ${!review.status ? 'pending' : 'approved'}`}>
                <div className="review-header">
                  <div className="review-user">
                    <h3>{review.user_name || `${review.user?.fname} ${review.user?.lname}`}</h3>
                    <div className="review-rating">
                      {renderStars(review.stars_count)}
                    </div>
                  </div>
                  <div className="review-status">
                    <span className={`status-badge ${review.status ? 'approved' : 'pending'}`}>
                      {review.status ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                </div>

                <div className="review-content">
                  <p>{review.comment}</p>
                </div>

                <div className="review-meta">
                  <div className="review-product">
                    <strong>Product:</strong> {review.product?.name || `Product ID: ${review.product_id}`}
                  </div>
                  <div className="review-date">
                    <strong>Date:</strong> {new Date(review.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="review-actions">
                  {!review.status && (
                    <button 
                      className="btn-approve"
                      onClick={() => handleApproveReview(review.id)}
                      disabled={actionLoading.approve === review.id}
                    >
                      {actionLoading.approve === review.id ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        "Approve"
                      )}
                    </button>
                  )}
                  {review.status && (
                    <button 
                      className="btn-reject"
                      onClick={() => handleRejectReview(review.id)}
                      disabled={actionLoading.reject === review.id}
                    >
                      {actionLoading.reject === review.id ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        "Reject"
                      )}
                    </button>
                  )}
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteReview(review.id)}
                    disabled={actionLoading.delete === review.id}
                  >
                    {actionLoading.delete === review.id ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="modal-overlay">
            <div className="modal-content delete-modal">
              <div className="modal-header">
                <div className="modal-title">
                  <svg className="warning-icon" viewBox="0 0 24 24">
                    <path d="M12 2l9 20H3l9-20z" fill="#ef4444"/>
                    <path d="M12 9v4M12 17h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h2>Delete Review</h2>
                </div>
                <button 
                  className="modal-close"
                  onClick={() => setDeleteModal(null)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <p className="confirmation-question">
                  Are you sure you want to delete this review?
                </p>
                <div className="review-preview">
                  <div className="review-preview-content">
                    <div className="review-preview-header">
                      <strong>{deleteModal.user_name || `${deleteModal.user?.fname} ${deleteModal.user?.lname}`}</strong>
                      <div className="review-preview-rating">
                        {renderStars(deleteModal.stars_count)}
                      </div>
                    </div>
                    <p className="review-preview-comment">"{deleteModal.comment}"</p>
                    <div className="review-preview-product">
                      <strong>Product:</strong> {deleteModal.product?.name || `Product ID: ${deleteModal.product_id}`}
                    </div>
                  </div>
                </div>
                
                <div className="warning-box">
                  <svg className="warning-icon-small" viewBox="0 0 24 24">
                    <path d="M12 2l9 20H3l9-20z" fill="#f59e0b"/>
                    <path d="M12 9v4M12 17h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>This action cannot be undone.</span>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={() => setDeleteModal(null)}
                  disabled={actionLoading.delete === deleteModal.id}
                >
                  Cancel
                </button>
                <button 
                  className="btn-delete-confirm"
                  onClick={confirmDeleteReview}
                  disabled={actionLoading.delete === deleteModal.id}
                >
                  {actionLoading.delete === deleteModal.id ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24">
                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2"/>
                        <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
